import React, { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import DropdownField from "../../components/Editing/DropdownField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { editActions } from "../../actions/editActions";
import { getDomainOptionsByDomainGroup } from "../../utils/pdApiUtils";
// eslint-disable-next-line
import { doorVersionHasChildren } from "../../utils/pdApiUtils";
import { escalatorVersionHasChildren } from "../../utils/pdApiUtils";

interface ErrorStatus {
  errorStatus: boolean;
  errorMsg: string;
}

const EditKCEVersionCommon: React.FC<{ toggleModal: () => void; target: string }> = ({
  toggleModal,
  target
}) => {
  const dispatch = useDispatch();

  // Redux selectors
  const VersionFields =
    target === "Door" ? nameofFactory<Api.DoorVersion>() : nameofFactory<Api.EscalatorVersion>();
  const editedVersion = useSelector((state: State.Root) => state.editView.editedObject);
  const storedVersion = useSelector((state: State.Root) => state.editView.storedObject);
  const pdApi = useSelector((state: State.Root) => state.pdApi);
  const activeUser = useSelector((state: State.Root) => state.main.activeUser);
  const domainOptions = getDomainOptionsByDomainGroup(pdApi, target as Api.GroupName);
  const apiType = target === "Door" ? "DoorVersions" : "EscalatorVersions";

  const isNew = !storedVersion;

  const deleteDisabled =
    storedVersion &&
    (target === "Door"
      ? doorVersionHasChildren(storedVersion.id, pdApi.doorParameters)
      : escalatorVersionHasChildren(storedVersion.id, pdApi.escalatorParameters, pdApi.escalatorFaults));

  if (domainOptions.length > 0 && isNew && editedVersion.domain.id === -1) {
    editedVersion.domain.id = domainOptions[0].key;
  }

  // Helper functions
  const isEmptyString = useCallback(
    (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0),
    []
  );

  const isFieldValid = useCallback(
    (field: string): ErrorStatus => {
      switch (field) {
        case VersionFields("versionName"): {
          const { versionName } = editedVersion;
          if (isEmptyString(versionName)) {
            return { errorStatus: false, errorMsg: "The name cannot be empty." };
          } else {
            const checkVersion = !_.values(pdApi[apiType]).find(
              version =>
                version.versionId !== editedVersion.versionId &&
                version.versionName.trim().toLowerCase() === versionName.trim().toLowerCase() &&
                isNew &&
                version.domain.id == editedVersion.domain.id
            );
            return { errorStatus: checkVersion, errorMsg: "The name must be unique in domain." };
          }
        }
        case VersionFields("number"): {
          const { number } = editedVersion;
          if (isEmptyString(number)) {
            return { errorStatus: false, errorMsg: "The number cannot be empty." };
          } else if (!new RegExp("^\\d{2}\\.\\d{2}\\.\\d{2}\\.\\d{2}$").test(number)) {
            return {
              errorStatus: false,
              errorMsg: "The number should be adhere to the numeric format: 00.00.00.00"
            };
          } else {
            const checkVersionID =
              _.values(pdApi[apiType]).filter(
                version =>
                  version.number == editedVersion.number &&
                  version.domain.id == editedVersion.domain.id &&
                  isNew
              ).length == 0
                ? true
                : false;

            return {
              errorStatus: checkVersionID,
              errorMsg: "The number must be unique in domain."
            };
          }
        }
        default:
          return { errorStatus: true, errorMsg: "" };
      }
    },
    [editedVersion, pdApi[apiType], isNew]
  );

  const isAllFieldsValid = useCallback((): boolean => {
    const temp = _.keys(editedVersion);
    for (const key of temp) {
      if (!isFieldValid(key).errorStatus) {
        return false;
      }
    }
    return true;
  }, [editedVersion, isFieldValid]);

  const checkVersionName = isFieldValid(VersionFields("versionName"));
  const checkVersionNumber = isFieldValid(VersionFields("number"));

  // Render component
  return (
    <Editor
      itemName={target === "Door" ? "door version" : "escalator version"}
      originalObject={storedVersion}
      editedObject={editedVersion}
      hasWritePermission={hasWriteAccess(activeUser, target as Api.GroupName)}
      isValid={isAllFieldsValid()}
      cancel={() => dispatch(editActions.cancelEditing())}
      update={() => dispatch(editActions.update(apiType as Api.OptionsGrud))}
      create={() => dispatch(editActions.add(apiType as Api.OptionsGrud))}
      remove={{
        callback: toggleModal,
        enabledTooltip: "version",
        disabled: deleteDisabled,
        disabledTooltip: ": Item linked to child components"
      }}
    >
      <DropdownField
        id="domainName"
        label="Domain name"
        disabled={!isNew}
        value={editedVersion.domain.id}
        options={domainOptions}
        search={true}
        onChange={id => {
          const domain = pdApi.domains[id];
          dispatch(editActions.setEdited({ ...editedVersion, domain }));
        }}
      />
      <TextField
        id="code"
        label="Number"
        value={editedVersion.number}
        isError={!checkVersionNumber.errorStatus}
        errorMessage={checkVersionNumber.errorMsg}
        readonly={!isNew}
        onChange={code =>
          dispatch(editActions.setEdited({ ...editedVersion, number: code.trim() }))
        }
      />
      <TextField
        id="name"
        label="Name"
        value={editedVersion.versionName}
        isError={!checkVersionName.errorStatus}
        errorMessage={checkVersionName.errorMsg}
        readonly={!isNew}
        onChange={name =>
          dispatch(editActions.setEdited({ ...editedVersion, versionName: name.trim() }))
        }
      />
      <TextField
        id="comment"
        label="Comment"
        value={editedVersion.description}
        onChange={comment =>
          dispatch(editActions.setEdited({ ...editedVersion, description: comment.trim() }))
        }
        readonly={!hasWriteAccess(activeUser, target as Api.GroupName)}
      />
    </Editor>
  );
};

export default EditKCEVersionCommon;
